// Copyright 2025 Sorama B.V.

import { autoinject } from "aurelia-dependency-injection";
import { DialogService } from "aurelia-dialog";
import { BindingEngine, computedFrom, Disposable, PLATFORM } from "aurelia-framework";
import { Router } from "aurelia-router";
import { ICurrentlyActivatedPackage } from "controls/package-badge/PackageBadge";
import { ActivatedLicenseDTO, LicenseBundle, SellableLicense } from "datatypes/SoramaWebApi";
import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { CamIV64PackageLicenseManager } from "managers/CamIV64PackageLicenseManager";
import { ConfirmationModel } from "modules/dialogs/Confirmation/ConfirmationDialog";
import { ErrorDialogModel } from "modules/dialogs/Error/ErrorDialog";
import { FactoryResetModel } from "modules/dialogs/FactoryReset/FactoryResetDialog";
import { FirmwareUpdateModel } from "modules/dialogs/FirmwareUpdate/FirmwareUpdateDialog";
import { RecoverSoftwarePackagesModel } from "modules/dialogs/RecoverSoftwarePackages/RecoverSoftwarePackagesDialog";
import { FirmwareRepository } from "repositories/FirmwareRepository";
import * as semver from "semver";
import { IClientService } from "services/Client/IClientService";
import { CamIv64Device } from "services/Hardware/CamHTTP/CamIV64Helpers";
import { CamIV64Service } from "services/Hardware/CamHTTP/CamIV64Service";
import { FirmwareInfo, ICamApi } from "services/Hardware/CamHTTP/ICamApi";
import { IHardwareDevice } from "services/Hardware/Datatypes/IHardwareDevice";
import { ILicenseService } from "services/License/ILicenseService";
import { ILicenseBundleService } from "services/LicenseBundle/ILicenseBundleService";

@autoinject
export class HomeDeviceInformation {
    private selectedCamIV64Id: string;
    private selectedCamIV64Type: string;
    private selectedCamIV64: CamIv64Device;
    private disposables: Disposable[] = [];
    private pendingPackageFileSendDeviceId: string = null;

    protected installedLicenses: ActivatedLicenseDTO[];
    protected devicePackages: ICurrentlyActivatedPackage[];
    protected installablePackages: LicenseBundle[];
    private licensesOnDevice: string[];
    private missingLicenses: ActivatedLicenseDTO[];
    protected isLoadingFirmware = true;
    protected isLoadingPackages = true;
    protected isWaitingForReboot = false;
    private installedVersion: FirmwareInfo;
    private remoteVersion: string;

    protected corePackage: ICurrentlyActivatedPackage = {
        name: "Core",
        tier: "Core",
        isLifetime: true,
        expirationDate: undefined,
        tag: "package_core",
    };

    private packageLicenseManager: CamIV64PackageLicenseManager;

    constructor(
        private readonly camIV64Service: CamIV64Service,
        private readonly bindingEngine: BindingEngine,
        private readonly clientService: IClientService,
        private readonly router: Router,
        private readonly licenseService: ILicenseService,
        private readonly licenseBundleService: ILicenseBundleService,
        private readonly camApi: ICamApi,
        private readonly firmwareRepository: FirmwareRepository,
        private readonly dialogService: DialogService
    ) {
        this.packageLicenseManager = new CamIV64PackageLicenseManager(
            this.licenseService,
            this.licenseBundleService,
            this.camApi,
            this.dialogService
        );
    }

    @computedFrom("installedVersion", "remoteVersion")
    public get isFirmwareUpdateAvailable(): boolean {
        if (!this.installedVersion || !this.remoteVersion) {
            return false;
        }

        try {
            const installedSemver = semver.clean(this.installedVersion.version);
            const remoteSemver = semver.clean(this.remoteVersion);

            if (installedSemver && remoteSemver) {
                return installedSemver !== remoteSemver;
            }
        } catch (error) {
            console.warn("Error comparing firmware versions with semver:", error);
        }

        return this.remoteVersion !== this.installedVersion.version;
    }

    @computedFrom("installedLicenses")
    protected get installedLicensesNoDuplicates(): ActivatedLicenseDTO[] {
        if (!this.installedLicenses) return [];

        return this.installedLicenses.filter((license, index, self) => {
            const firstIndex = self.findIndex(
                (l) => l.LicenseKind?.LicenseKindTag === license.LicenseKind?.LicenseKindTag
            );
            return firstIndex === index;
        });
    }

    @computedFrom("devicePackages")
    protected get devicePackagesNoDuplicates(): ICurrentlyActivatedPackage[] {
        if (!this.devicePackages) return [];

        return this.devicePackages.filter((pkg, index, self) => {
            const firstIndex = self.findIndex((p) => p.tag === pkg.tag);
            return firstIndex === index;
        });
    }

    @computedFrom("devicePackages")
    protected get isWebshopReferenceEnabled(): boolean {
        if (!this.devicePackages) return false;
        return !this.devicePackages.some((x) => x.name.toLowerCase().includes("ultimate"));
    }

    public async bind(): Promise<void> {
        this.disposables.push(
            this.bindingEngine
                .propertyObserver(this.camIV64Service, "selectedDeviceId")
                .subscribe(this.handleSelectedDeviceChanged),
            this.bindingEngine
                .propertyObserver(this.camIV64Service, "hardware")
                .subscribe(this.handleCamIV64HardwareChanged)
        );

        // Initialize with the currently selected device if one is already set
        const currentId = this.camIV64Service.selectedDeviceId;
        if (currentId) {
            await this.loadSelectedDevice(currentId);
        }
    }

    public unbind(): void {
        while (this.disposables.length) {
            const d = this.disposables.pop();
            if (d) {
                d.dispose();
            }
        }
    }

    private handleSelectedDeviceChanged = async (newId: string): Promise<void> => {
        if (newId) {
            await this.loadSelectedDevice(newId);
        } else {
            this.clearDeviceInfo();
        }
    };

    private handleCamIV64HardwareChanged = (newHardwareList: IHardwareDevice[] | undefined): void => {
        const allDevices = newHardwareList ? [...newHardwareList] : [];

        // If the selected device disappeared, clear info
        const stillPresent = allDevices.some((d) => d.id === this.selectedCamIV64Id);
        if (!stillPresent) {
            this.clearDeviceInfo();
            return;
        }

        this.isWaitingForReboot = false;

        // If a device that needed a package file send after reboot is back online, send it now
        if (
            this.pendingPackageFileSendDeviceId &&
            allDevices.some((d) => d.id === this.pendingPackageFileSendDeviceId)
        ) {
            const deviceId = this.pendingPackageFileSendDeviceId;
            this.pendingPackageFileSendDeviceId = null;
            this.camIV64Service.getHardwareById(deviceId).then((device) => {
                if (device) {
                    this.packageLicenseManager.sendPackageFileToDevice(device);
                }
            });
        }

        if (this.selectedCamIV64Id) {
            this.loadCamIV64Device(this.selectedCamIV64Id);
        }
    };

    private clearDeviceInfo(): void {
        this.selectedCamIV64Id = undefined;
        this.selectedCamIV64 = null;
        this.selectedCamIV64Type = undefined;
        this.installedLicenses = [];
        this.devicePackages = [];
        this.installablePackages = [];
        this.missingLicenses = [];
        this.installedVersion = null;
        this.remoteVersion = null;
    }

    private async loadSelectedDevice(camId: string): Promise<void> {
        this.isLoadingFirmware = true;
        this.installedLicenses = [];
        this.missingLicenses = [];
        this.installedVersion = null;
        this.remoteVersion = null;
        this.isLoadingPackages = true;

        this.selectedCamIV64Id = camId;
        await this.loadCamIV64Device(camId);
    }

    private async loadCamIV64Device(camId: string): Promise<void> {
        try {
            this.selectedCamIV64 = await this.camIV64Service.getHardwareById(camId);
            this.selectedCamIV64Type = this.selectedCamIV64?.type;

            this.loadInstalledLicenses();
            this.loadDeviceFirmware();
            this.loadRemoteFirmware();
        } catch (e) {
            console.error("Failed to get selected CAM's information", e);
        }
    }

    private async loadRemoteFirmware(): Promise<void> {
        try {
            if (!this.selectedCamIV64?.type) {
                throw new Error("Could not determine device type for firmware update.");
            }
            this.remoteVersion = await this.firmwareRepository.getLatestVersion(this.selectedCamIV64.type);
        } catch (e) {
            this.remoteVersion = undefined;
            console.error("Could not fetch the latest firmware version: " + e.message);
        }
    }

    private async loadDeviceFirmware(): Promise<void> {
        try {
            this.isLoadingFirmware = true;
            this.installedVersion = await this.camApi.getDeviceFirmware();
        } catch (error) {
            console.error("Failed to load firmware information", error);
        } finally {
            this.isLoadingFirmware = false;
        }
    }

    private async loadInstalledLicenses(): Promise<void> {
        if (!this.selectedCamIV64) return;
        const state = await this.packageLicenseManager.loadInstalledLicenses(this.selectedCamIV64);
        this.installablePackages = state.installablePackages;
        this.devicePackages = state.devicePackages;
        this.installedLicenses = state.installedLicenses;
        this.missingLicenses = state.missingLicenses;
        this.isLoadingPackages = state.isLoadingPackages;
    }

    private createPackageFromBundle(bundle: LicenseBundle): ICurrentlyActivatedPackage {
        return this.packageLicenseManager.createPackageFromBundle(bundle);
    }

    private navigate(): void {
        this.router.navigate(this.selectedCamIV64Type ? `/webshop?deviceType=${this.selectedCamIV64Type}` : "/webshop");
    }

    public async downloadLogs(): Promise<void> {
        const url = await this.camApi.getLogUrl();
        const extension = url.split(".").pop() || "log";
        FileDownloader.downloadAsFileFromURL(
            url,
            `${this.selectedCamIV64Id}_logs_${new Date().toISOString()}.${extension}`
        );
    }

    public async updateFirmware(): Promise<void> {
        if (!this.installedVersion || !this.remoteVersion || !this.selectedCamIV64) {
            console.error("Missing firmware or device information");
            return;
        }

        const model: FirmwareUpdateModel = {
            device: this.selectedCamIV64,
            installedVersion: this.installedVersion,
            remoteVersion: this.remoteVersion,
        };

        try {
            await this.dialogService
                .open({
                    viewModel: PLATFORM.moduleName("modules/dialogs/FirmwareUpdate/FirmwareUpdateDialog"),
                    model,
                    lock: true,
                })
                .whenClosed(async (response) => {
                    if (!response.wasCancelled) {
                        await this.loadDeviceFirmware();
                        await this.loadRemoteFirmware();
                        this.pendingPackageFileSendDeviceId = this.selectedCamIV64Id;
                        this.isWaitingForReboot = true;
                    }
                });
        } catch (error) {
            console.error("Firmware update failed:", error);
        }
    }

    public async performFactoryReset(): Promise<void> {
        if (!this.selectedCamIV64) {
            console.error("No CAM IV64 device selected for factory reset");
            return;
        }

        const model: FactoryResetModel = {
            device: this.selectedCamIV64,
        };

        try {
            await this.dialogService
                .open({
                    viewModel: PLATFORM.moduleName("modules/dialogs/FactoryReset/FactoryResetDialog"),
                    model,
                    lock: true,
                })
                .whenClosed(async (response) => {
                    if (!response.wasCancelled) {
                        await this.loadDeviceFirmware();
                        this.pendingPackageFileSendDeviceId = this.selectedCamIV64Id;
                        this.isWaitingForReboot = true;
                    }
                });
        } catch (error) {
            console.error("Factory reset failed:", error);
        }
    }

    private async clickedPackage(installablePackage: LicenseBundle): Promise<void> {
        const { Tier: tier = "", Name: baseName = "Unknown package" } = installablePackage.LicenseBundleKind || {};
        const name = tier.trim().toLowerCase() == "expert" ? `Expert - ${baseName}` : baseName;

        await this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
                model: <ConfirmationModel>{
                    title: `Install package ${baseName}`,
                    text: `Are you sure you want to install the "${name}" package on your Sorama CAM ${this.selectedCamIV64.type} with serial number ${this.selectedCamIV64.serialNumber}?`,
                    confirmText: "Install",
                    operation: () => this.installPackage(installablePackage),
                },
                lock: true,
            })
            .whenClosed(async (result) => {
                if (result.wasCancelled) {
                    if (result.output) {
                        await this.dialogService.open({
                            viewModel: PLATFORM.moduleName("modules/dialogs/Error/ErrorDialog"),
                            model: <ErrorDialogModel>{
                                text: result.output,
                            },
                        });
                    }
                } else {
                    await this.loadInstalledLicenses();
                }
                this.licenseService.reloadSellableFeatureLicenses();
            });
    }

    private async installPackage(installablePackage: LicenseBundle): Promise<void> {
        if (!this.selectedCamIV64) return;
        await this.packageLicenseManager.installPackage(installablePackage, this.selectedCamIV64);
    }

    private async clickedFeatureLicense(license: SellableLicense): Promise<void> {
        await this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
                model: <ConfirmationModel>{
                    title: `Activate ${license.LicenseKind.LicenseKindName}`,
                    text: `Are you sure you want to activate the ${license.LicenseKind.LicenseKindName} license on this device?`,
                    confirmText: "Activate",
                    operation: () => this.activateLicense(license),
                },
                lock: true,
            })
            .whenClosed(async (result) => {
                if (result.wasCancelled) {
                    if (result.output) {
                        await this.dialogService.open({
                            viewModel: PLATFORM.moduleName("modules/dialogs/Error/ErrorDialog"),
                            model: <ErrorDialogModel>{
                                text: result.output,
                            },
                        });
                    }
                } else {
                    await this.loadInstalledLicenses();
                }
                this.licenseService.reloadSellableFeatureLicenses();
            });
    }

    private async activateLicense(license: SellableLicense): Promise<void> {
        if (!this.selectedCamIV64) return;
        await this.packageLicenseManager.activateLicense(license, this.selectedCamIV64);
    }

    private async clickedPackageRecovery(): Promise<void> {
        const hardwareClock = await this.camApi.getHardwareClock();
        await this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/RecoverSoftwarePackages/RecoverSoftwarePackagesDialog"),
                model: <RecoverSoftwarePackagesModel>{
                    hardwareClock: hardwareClock,
                    missingLicenses: this.missingLicenses,
                    missingPackages: this.installablePackages,
                    device: this.selectedCamIV64,
                },
                lock: true,
            })
            .whenClosed(async (result) => {
                if (result.wasCancelled) {
                    if (result.output) {
                        await this.dialogService.open({
                            viewModel: PLATFORM.moduleName("modules/dialogs/Error/ErrorDialog"),
                            model: <ErrorDialogModel>{
                                text: result.output,
                            },
                        });
                    }
                } else {
                    await this.sendPackageFileToDevice();
                    await this.loadInstalledLicenses();
                    this.licenseService.reloadSellableFeatureLicenses();
                }
            });
    }

    private async sendPackageFileToDevice(): Promise<void> {
        if (!this.selectedCamIV64) return;
        await this.packageLicenseManager.sendPackageFileToDevice(this.selectedCamIV64);
    }
}
